import type { IqcStatus } from "./inboundApi";

export type InboundIqcQuantityFields = {
  iqc_passed_quantity: string;
  iqc_failed_quantity: string;
  iqc_skipped_quantity: string;
};

export function parseIqcQuantity(value: string): number {
  const trimmed = value.trim();
  if (trimmed === "") return 0;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function iqcQuantityTotal(fields: InboundIqcQuantityFields): number {
  return (
    parseIqcQuantity(fields.iqc_passed_quantity) +
    parseIqcQuantity(fields.iqc_failed_quantity) +
    parseIqcQuantity(fields.iqc_skipped_quantity)
  );
}

export function isIqcDispositionComplete(countedQuantity: number, fields: InboundIqcQuantityFields): boolean {
  if (countedQuantity <= 0) return false;
  return iqcQuantityTotal(fields) === countedQuantity;
}

export function deriveIqcStatus(countedQuantity: number, fields: InboundIqcQuantityFields): IqcStatus {
  const pass = parseIqcQuantity(fields.iqc_passed_quantity);
  const fail = parseIqcQuantity(fields.iqc_failed_quantity);
  const skipped = parseIqcQuantity(fields.iqc_skipped_quantity);
  const total = pass + fail + skipped;

  if (countedQuantity <= 0 || total === 0) return "pending";
  if (total < countedQuantity) return "pending";

  if (fail === 0 && skipped === 0 && pass === countedQuantity) return "passed";
  if (pass === 0 && skipped === 0 && fail === countedQuantity) return "failed";
  if (pass === 0 && fail === 0 && skipped === countedQuantity) return "skipped";
  if (fail > 0) return "failed";
  if (pass > 0) return "passed";
  return "skipped";
}

export function formatIqcSummary(fields: InboundIqcQuantityFields): string | null {
  const pass = parseIqcQuantity(fields.iqc_passed_quantity);
  const fail = parseIqcQuantity(fields.iqc_failed_quantity);
  const skipped = parseIqcQuantity(fields.iqc_skipped_quantity);
  if (pass === 0 && fail === 0 && skipped === 0) return null;

  const parts: string[] = [];
  if (pass > 0) parts.push(`${String(pass)}P`);
  if (fail > 0) parts.push(`${String(fail)}F`);
  if (skipped > 0) parts.push(`${String(skipped)}S`);
  return parts.join(" · ");
}

export function validateIqcQuantities(
  countedQuantity: number,
  fields: InboundIqcQuantityFields,
): { ok: true } | { ok: false; message: string } {
  if (countedQuantity <= 0) {
    return { ok: false, message: "Count this line before recording IQC." };
  }
  const pass = parseIqcQuantity(fields.iqc_passed_quantity);
  const fail = parseIqcQuantity(fields.iqc_failed_quantity);
  const skipped = parseIqcQuantity(fields.iqc_skipped_quantity);
  const total = pass + fail + skipped;

  if (total === 0) {
    return { ok: false, message: "Enter pass, fail, or skipped quantities." };
  }
  if (total > countedQuantity) {
    return {
      ok: false,
      message: `Pass + fail + skipped (${String(total)}) cannot exceed counted quantity (${String(countedQuantity)}).`,
    };
  }
  if (total < countedQuantity) {
    return {
      ok: false,
      message: `Account for all ${String(countedQuantity)} units (currently ${String(total)}).`,
    };
  }
  return { ok: true };
}

export function iqcQuantityFieldsFromNumbers(input: {
  passed: number | null | undefined;
  failed: number | null | undefined;
  skipped: number | null | undefined;
}): InboundIqcQuantityFields {
  const format = (value: number | null | undefined): string => {
    if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) return "";
    return String(Math.trunc(Number(value)));
  };
  return {
    iqc_passed_quantity: format(input.passed),
    iqc_failed_quantity: format(input.failed),
    iqc_skipped_quantity: format(input.skipped),
  };
}
